import { AlertTriangle, CreditCard, XCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useCredits } from "@/hooks/useCredits";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";

export const SubscriptionBanner = () => {
  const { subscription_status, grace_period_end, downgraded_at, isPastDue, isEffectivelyDowngraded, gracePeriodDaysRemaining } = useCredits();
  const [opening, setOpening] = useState(false);

  const openBillingPortal = async () => {
    setOpening(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch {
      // silent
    } finally {
      setOpening(false);
    }
  };

  // Past due but still in grace period
  if (isPastDue() && !isEffectivelyDowngraded()) {
    const daysLeft = gracePeriodDaysRemaining();
    return (
      <Alert variant="destructive" className="rounded-none border-x-0 border-t-0">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between w-full">
          <span>
            Your payment failed. Update your payment method within <strong>{daysLeft} day{daysLeft !== 1 ? "s" : ""}</strong> to keep Pro features.
          </span>
          <Button size="sm" variant="outline" onClick={openBillingPortal} disabled={opening}>
            <CreditCard className="h-3 w-3 mr-1" />
            Update Payment
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Grace period expired or canceled
  if (isEffectivelyDowngraded() && (subscription_status === "past_due" || subscription_status === "canceled")) {
    const isCanceled = subscription_status === "canceled";
    return (
      <Alert variant="destructive" className="rounded-none border-x-0 border-t-0">
        <XCircle className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between w-full">
          <span>
            {isCanceled
              ? "Your Pro subscription has ended. Your remaining call credit is still available. Custom agents are read-only."
              : "Your Pro subscription has been suspended due to overdue payment. Update payment to restore access."}
          </span>
          <Button size="sm" variant="outline" onClick={openBillingPortal} disabled={opening}>
            <CreditCard className="h-3 w-3 mr-1" />
            {isCanceled ? "Resubscribe" : "Update Payment"}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return null;
};
